import { VoiceCrossFrameWrapper } from '@services/voice/VoiceCrossFrameWrapper';

const voiceCrossFrameWrapper = new VoiceCrossFrameWrapper();

/**
 * Client for the SpeechRecognitionService in base tool. {@see SpeechRecognitionService} in base tool
 * for more documentation on how this client can be used and how it can fail.
 */
export class SpeechRecognitionClient {
    /**
     * Warms up the speech speech recognition client so that the first call to speechRecognition
     * doesn't get impacted by this latency.
     *
     * The caller of this public static is encouraged to catch any exceptions and ignore them since it
     * could be a transient issue and a call to speechRecognition might still work if the transient
     * issue has been resolved (with some added latency). This is unless you get an
     * UnsupportedBrowserException.
     *
     * @return {Promise} Resolves when warmup finishes successfully and rejects when it doesn't.
     */
    public static warmup() {
        return SpeechRecognitionClient.makeCrossFrameCall('warmup');
    }

    /**
     * Calls speechRecognitionService getAlphanumeric with the specified config.
     * @param {object} config - optional argument to configure the call.
     * @return {Promise} Resolves with a {ResponseObject} (see docs in base tool).
     */
    public static getAlphanumeric(config) {
        return SpeechRecognitionClient.makeCrossFrameCall('getAlphanumeric', config);
    }

    /**
     * Calls speechRecognitionService.getConfirmation with the specified config.
     * @param {object} config - optional argument to configure the call.
     * @return {Promise} Resolves with a {ResponseObject} (see docs in base tool).
     */
    public static getConfirmation(config: any) {
        return SpeechRecognitionClient.makeCrossFrameCall('getConfirmation', config);
    }

    /**
     * Calls speechRecognitionService.getCustomDataType with the specified config.
     * @param {object} config - required and must have the 'dataType' key.
     * @return {Promise} Resolves with a {ResponseObject} (see docs in base tool).
     */
    public static getCustomDataType(config: any) {
        return SpeechRecognitionClient.makeCrossFrameCall('getCustomDataType', config);
    }

    /**
     * Calls speechRecognitionService.getDate with the specified config.
     * @param {object} config - optional argument to configure the call.
     * @return {Promise} Resolves with a {ResponseObject} (see docs in base tool).
     */
    public static getDate(config: any) {
        return SpeechRecognitionClient.makeCrossFrameCall('getDate', config);
    }

    /**
     * Calls speechRecognitionService.getNumber with the specified config.
     * @param {object} config - optional argument to configure the call.
     * @return {Promise} Resolves with a {ResponseObject} (see docs in base tool).
     */
    public static getNumber(config: any) {
        return SpeechRecognitionClient.makeCrossFrameCall('getNumber', config);
    }

    /**
     * Calls speechRecognitionService.getPositiveInteger with the specified config.
     * @param {object} config - optional argument to configure the call.
     * @return {Promise} Resolves with a {ResponseObject} (see docs in base tool).
     */
    public static getPositiveInteger(config: any) {
        return SpeechRecognitionClient.makeCrossFrameCall('getPositiveInteger', config);
    }

    /**
     * Makes the cross frame call to base tool.
     */
    private static makeCrossFrameCall(action: string, parameters?: any) {
        return voiceCrossFrameWrapper.call('speechRecognitionListener', action, parameters);
    }
}
